import { LOGIN_SUCCESS, LOGOUT, SESSION_VALID } from '@comeon/actions-public';
import reducerRegistry from '@comeon/reducer-registry';

import type { CasinoSharedThunkAction } from '~/components/casino-shared/ducks/types';
import { EnhancedGame, Game } from '~/components/casino-shared/types';
import enhanceImageView from '~/components/casino-shared/utilities/enhanceImage';
import getGameUrls from '~/components/casino-shared/utilities/getGameUrls';
import { createImageViewFactory } from '~/components/utilities/image-format';
import { parseResponse, requestApi } from '~/services/api-new';
import { ADD_GAME, FETCH_CASINO_ALL_GAMES_FINISH, FETCH_CASINO_ALL_GAMES_START } from './action-names';
import { Actions, State } from './types';

const REDUCER_NAME = 'allGames';
const host = '/';

const fetchAllGames =
	(): CasinoSharedThunkAction =>
	async (dispatch, getState, { imageDescriptors, imageFormats, license }) => {
		const state = getState();

		const {
			brand: { isDesktop },
			authentication: { authenticated },
			allGames: { hydrated, loading }
		} = state;

		if (hydrated || loading) {
			return;
		}

		dispatch({
			type: FETCH_CASINO_ALL_GAMES_START
		});

		const resourcePath = `rest/state/casino/games/v2?dummy=${isDesktop ? 1 : 0}${authenticated ? 1 : 0}`;

		const response = parseResponse<Game[]>(
			await requestApi({
				endpoint: `${host}${resourcePath}`,
				additionalOptions: {
					cache: 'default'
				}
			})
		);

		if ('error' in response) {
			return;
		}

		dispatch({
			type: FETCH_CASINO_ALL_GAMES_FINISH,
			payload: {
				response,
				imageDescriptors,
				imageFormats,
				license
			}
		});
	};

const addGame = (game: EnhancedGame): Actions => ({
	type: ADD_GAME,
	payload: game
});

const initialState: State = {
	hydrated: false,
	loading: false,
	games: {},
	gameCodesByProviderCode: {},
	gameCodesBySubProviderCode: {},
	gameNamesByIdx: [],
	gameCodesByIdx: [],
	gameIdxByProvider: {},
	gameIdxByFilter: {},
	gameCodesByCategory: {},
	providers: {},
	subProviders: {}
};

const reducer = (stateTemp = initialState, action: Actions) => {
	const state = stateTemp.initialized ? stateTemp : { ...initialState, ...stateTemp, initialized: true };
	switch (action.type) {
		case LOGIN_SUCCESS:
		case SESSION_VALID:
		case LOGOUT:
			return {
				...state
			};
		case FETCH_CASINO_ALL_GAMES_START:
			return {
				...state,
				loading: true,
				hydrated: false,
				gameNamesByIdx: [],
				gameCodesByIdx: [],
				gameIdxByProvider: {},
				gameCodesByCategory: {},
				gameIdxByFilter: {},
				gameCodesByProviderCode: {},
				gameCodesBySubProviderCode: {}
			};
		case FETCH_CASINO_ALL_GAMES_FINISH: {
			const gameCodesByProviderCode = { ...state.gameCodesByProviderCode };
			const gameCodesBySubProviderCode = { ...state.gameCodesBySubProviderCode };
			const gameNamesByIdx = state.gameNamesByIdx.slice();
			const gameCodesByIdx = state.gameCodesByIdx.slice();
			const gameIdxByProvider = { ...state.gameIdxByProvider };

			const providers = { ...state.providers };
			const subProviders = { ...state.subProviders };

			const gameCodesByCategory = { ...state.gameCodesByCategory };
			const gameIdxByFilter = { ...state.gameIdxByFilter };
			const { imageDescriptors, imageFormats, response, license } = action.payload;

			const formatEnhancer = createImageViewFactory(imageFormats, imageDescriptors);

			const fetchedGames = response?.reduce<Record<string, EnhancedGame>>((acc, currGame, index) => {
				const game: EnhancedGame = {
					...currGame,
					...getGameUrls(currGame, license),
					imageView: enhanceImageView(currGame.imageView, imageDescriptors, formatEnhancer)
				};

				gameNamesByIdx[index] = game.name;
				gameCodesByIdx[index] = game.code;

				if (game.jackpot) {
					if (gameIdxByFilter.jackpot) {
						gameIdxByFilter.jackpot.push(index);
					} else {
						gameIdxByFilter.jackpot = [index];
					}
				}

				if (game.isNew) {
					if (gameIdxByFilter.isNew) {
						gameIdxByFilter.isNew.push(index);
					} else {
						gameIdxByFilter.isNew = [index];
					}
				}

				if (game.isHot) {
					if (gameIdxByFilter.isHot) {
						gameIdxByFilter.isHot.push(index);
					} else {
						gameIdxByFilter.isHot = [index];
					}
				}

				if (gameIdxByProvider[game.provider.name]) {
					gameIdxByProvider[game.provider.name].push(index);
				} else {
					gameIdxByProvider[game.provider.name] = [index];
				}

				if (gameCodesByProviderCode[game.provider.code]) {
					gameCodesByProviderCode[game.provider.code].push(game.code);
				} else {
					gameCodesByProviderCode[game.provider.code] = [game.code];
				}

				if (game.casinoSubProviderVO) {
					if (gameCodesBySubProviderCode[game.casinoSubProviderVO.code]) {
						gameCodesBySubProviderCode[game.casinoSubProviderVO.code].push(game.code);
					} else {
						gameCodesBySubProviderCode[game.casinoSubProviderVO.code] = [game.code];
					}
					subProviders[game.casinoSubProviderVO.code] = game.casinoSubProviderVO;
				}

				if (gameCodesByCategory[game.categoryCode]) {
					gameCodesByCategory[game.categoryCode].push(game.code);
				} else {
					gameCodesByCategory[game.categoryCode] = [game.code];
				}

				providers[game.provider.code] = { ...game.provider };

				acc[game.code] = {
					...game
				};

				return acc;
			}, {});

			return {
				...state,
				games: {
					...state.games,
					...fetchedGames
				},
				gameNamesByIdx,
				gameCodesByIdx,
				gameIdxByProvider,
				gameCodesByProviderCode,
				gameCodesBySubProviderCode,
				gameCodesByCategory,
				gameIdxByFilter,
				providers,
				subProviders,
				hydrated: true,
				loading: false
			};
		}
		case ADD_GAME: {
			return {
				...state,
				games: {
					...state.games,
					[action.payload.code]: action.payload
				}
			};
		}
		default:
			return state;
	}
};

reducerRegistry.register(REDUCER_NAME, reducer);

export { fetchAllGames, addGame };

export default reducer;
